/**
 * SessionsPanel — Superadmin-only real-time session analytics.
 *
 * Polls `/api/admin/analytics/sessions` every 30 seconds so the panel
 * always reflects who is currently on the platform. Backend is gated
 * with `require_superadmin`, so plain admins never see live data here
 * even if they somehow render this tab.
 */
import React, { useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Users, Activity, RefreshCw, Shield, TrendingUp, Clock, MapPin, Loader2, Wifi, UserCog,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import ImpersonateDialog from "@/components/ImpersonateDialog";

const POLL_MS = 30_000;
const ROLE_COLOR = {
  superadmin: "bg-red-100 text-red-800",
  admin: "bg-orange-100 text-orange-800",
  vendor: "bg-emerald-100 text-emerald-800",
  consumer: "bg-blue-100 text-blue-800",
};

export default function SessionsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [impersonateTarget, setImpersonateTarget] = useState(null);
  const { user: currentUser, refresh: refreshAuth } = useAuth();

  const impersonate = (row) => {
    if (row.role === "superadmin") {
      toast.info("Cannot impersonate another superadmin.");
      return;
    }
    if (row.user_id === currentUser?.user_id) {
      toast.info("You are already signed in as this user.");
      return;
    }
    setImpersonateTarget(row);
  };

  const onImpersonateSuccess = async () => {
    await refreshAuth();
    window.location.href = "/";
  };

  const load = useCallback(async (isBackground = false) => {
    if (!isBackground) setRefreshing(true);
    try {
      const { data: d } = await api.get("/admin/analytics/sessions");
      setData(d);
      setError(null);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Failed to load session analytics";
      setError(detail);
      if (!isBackground) toast.error(detail);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(() => load(true), POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  const maxHourly = useMemo(() => {
    if (!data?.hourly_activity) return 1;
    return Math.max(1, ...data.hourly_activity.map((h) => h.count));
  }, [data]);

  if (loading && !data) {
    return (
      <div className="p-10 text-center text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin inline-block mr-2 align-middle" />
        Loading session analytics…
      </div>
    );
  }

  if (error && !data) {
    return (
      <Card className="p-8 border-destructive/50 bg-destructive/5" data-testid="sessions-panel-error">
        <div className="flex items-center gap-2 text-destructive font-medium mb-1">
          <Shield className="h-5 w-5" /> {error}
        </div>
        <p className="text-xs text-muted-foreground">
          This panel is superadmin-only. If you believe this is a mistake, ask a superadmin to grant your role.
        </p>
      </Card>
    );
  }

  if (!data) return null;

  return (
    <div className="space-y-5" data-testid="sessions-panel">
      {/* Header row */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-primary font-semibold flex items-center gap-2">
            <Activity className="h-3.5 w-3.5" /> Live session analytics · superadmin-only
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Auto-refreshes every {POLL_MS / 1000}s · online window: {data.online_window_min} min ·
            generated {new Date(data.generated_at).toLocaleTimeString()}
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          className="rounded-full h-8"
          onClick={() => load(false)}
          disabled={refreshing}
          data-testid="sessions-refresh-btn"
        >
          {refreshing ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
          Refresh
        </Button>
      </div>

      {/* Top stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <BigStat
          label="Online now"
          value={data.online_now}
          icon={Wifi}
          accent="text-emerald-600"
          testId="sessions-stat-online"
          hint={`≤ ${data.online_window_min}m activity`}
        />
        <BigStat label="DAU · 24h" value={data.dau} icon={Users} accent="text-sky-600" testId="sessions-stat-dau" />
        <BigStat label="WAU · 7d" value={data.wau} icon={TrendingUp} accent="text-violet-600" testId="sessions-stat-wau" />
        <BigStat label="MAU · 30d" value={data.mau} icon={TrendingUp} accent="text-fuchsia-600" testId="sessions-stat-mau" />
        <BigStat label="Total sessions" value={data.totals.total_sessions} icon={Activity} testId="sessions-stat-total" />
        <BigStat label="Total users" value={data.totals.total_users} icon={Users} testId="sessions-stat-users" hint={`${data.totals.total_approved_users} approved`} />
      </div>

      {/* Breakdown row */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card className="p-4" data-testid="sessions-role-breakdown">
          <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground font-semibold mb-3">
            Online now — by role
          </div>
          {data.role_breakdown.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">Nobody online right now.</p>
          ) : (
            <div className="space-y-2">
              {data.role_breakdown.map((r) => (
                <div key={r.role} className="flex items-center gap-3">
                  <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full w-24 text-center ${ROLE_COLOR[r.role] || "bg-muted text-foreground/80"}`}>
                    {r.role}
                  </span>
                  <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                    <div
                      className="h-full rounded-full bg-primary"
                      style={{ width: `${Math.min(100, (r.count / Math.max(1, data.online_now)) * 100)}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium w-8 text-right">{r.count}</span>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-4" data-testid="sessions-city-breakdown">
          <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground font-semibold mb-3">
            Online now — by city (top 10)
          </div>
          {data.city_breakdown.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">No city data available.</p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {data.city_breakdown.map((c) => (
                <span
                  key={c.city}
                  className="inline-flex items-center gap-1 rounded-full bg-muted/70 border border-border/60 px-2.5 py-1 text-xs"
                >
                  <MapPin className="h-3 w-3 text-primary" /> {c.city}
                  <span className="ml-1 text-[10px] font-bold text-primary">{c.count}</span>
                </span>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Hourly heatmap */}
      <Card className="p-4" data-testid="sessions-hourly">
        <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground font-semibold mb-3">
          Activity by hour · last 7 days (UTC)
        </div>
        <div className="flex items-end gap-1 h-24">
          {data.hourly_activity.map((h) => {
            const heightPct = Math.max(2, (h.count / maxHourly) * 100);
            return (
              <div key={h.hour} className="flex-1 group relative">
                <div
                  className="w-full rounded-t-sm bg-primary/60 hover:bg-primary transition-colors"
                  style={{ height: `${heightPct}%` }}
                  data-testid={`sessions-hour-${h.hour}`}
                  title={`${h.hour.toString().padStart(2, "0")}:00 UTC · ${h.count} sessions`}
                />
                <div className="text-[9px] text-center text-muted-foreground mt-1">
                  {h.hour.toString().padStart(2, "0")}
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Now-online table */}
      <Card className="p-4" data-testid="sessions-live-table">
        <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground font-semibold mb-3 flex items-center gap-2">
          <Wifi className="h-3.5 w-3.5 text-emerald-600" /> Live sessions ({data.now_online.length})
        </div>
        {data.now_online.length === 0 ? (
          <p className="text-sm text-muted-foreground italic">No sessions active in the last {data.online_window_min} minutes.</p>
        ) : (
          <div className="overflow-x-auto -mx-2">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-muted-foreground border-b border-border/60">
                  <th className="p-2 font-medium">User</th>
                  <th className="p-2 font-medium">Role</th>
                  <th className="p-2 font-medium">City</th>
                  <th className="p-2 font-medium">Last seen</th>
                  <th className="p-2 font-medium">Session started</th>
                  <th className="p-2 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.now_online.map((s) => (
                  <tr key={`${s.user_id}-${s.session_created_at}`} className="border-b border-border/40" data-testid={`sessions-live-row-${s.user_id}`}>
                    <td className="p-2">
                      <div className="flex items-center gap-2">
                        <Avatar className="h-7 w-7">
                          <AvatarImage src={s.picture} />
                          <AvatarFallback className="text-[10px]">{(s.name || "?")[0]}</AvatarFallback>
                        </Avatar>
                        <div className="min-w-0">
                          <div className="font-medium truncate">{s.name}</div>
                          <div className="text-[10px] text-muted-foreground truncate">{s.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="p-2">
                      <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full ${ROLE_COLOR[s.role] || "bg-muted text-foreground/80"}`}>
                        {s.role}
                      </span>
                    </td>
                    <td className="p-2 text-muted-foreground">{s.city || "—"}</td>
                    <td className="p-2 text-muted-foreground">{relativeTime(s.last_seen_at)}</td>
                    <td className="p-2 text-muted-foreground">{relativeTime(s.session_created_at)}</td>
                    <td className="p-2 text-right">
                      {s.role !== "superadmin" && s.user_id !== currentUser?.user_id ? (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 border-purple-500/60 text-purple-700 hover:bg-purple-50 dark:hover:bg-purple-950/30"
                          onClick={() => impersonate(s)}
                          data-testid={`sessions-impersonate-${s.user_id}`}
                          title="Sign in as this user (2h session, audited)"
                        >
                          <UserCog className="h-3.5 w-3.5 mr-1" /> Sign in as
                        </Button>
                      ) : (
                        <span className="text-[10px] text-muted-foreground italic">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Recent logins */}
      <Card className="p-4" data-testid="sessions-recent-logins">
        <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground font-semibold mb-3 flex items-center gap-2">
          <Clock className="h-3.5 w-3.5" /> Recent logins · newest {data.recent_logins.length}
        </div>
        <div className="space-y-1">
          {data.recent_logins.map((s, i) => (
            <div key={i} className="flex items-center gap-3 py-1.5 border-b border-border/40 last:border-b-0" data-testid={`sessions-recent-row-${i}`}>
              <Avatar className="h-7 w-7">
                <AvatarImage src={s.picture} />
                <AvatarFallback className="text-[10px]">{(s.name || "?")[0]}</AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{s.name} <span className="text-[10px] text-muted-foreground font-normal">· {s.email}</span></div>
                <div className="text-[10px] text-muted-foreground">
                  {s.role} · {s.city || "no city"} · signed in {relativeTime(s.session_created_at)}
                </div>
              </div>
              <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full ${ROLE_COLOR[s.role] || "bg-muted text-foreground/80"}`}>
                {s.role}
              </span>
            </div>
          ))}
        </div>
      </Card>

      <ImpersonateDialog
        open={!!impersonateTarget}
        onOpenChange={(o) => !o && setImpersonateTarget(null)}
        target={impersonateTarget}
        onSuccess={onImpersonateSuccess}
      />
    </div>
  );
}

function BigStat({ label, value, icon: Icon, accent = "text-primary", hint, testId }) {
  return (
    <Card className="p-3" data-testid={testId}>
      <div className="flex items-center gap-2">
        <Icon className={`h-4 w-4 ${accent}`} />
        <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground truncate">{label}</div>
      </div>
      <div className="text-2xl font-heading tracking-tight mt-1">{value}</div>
      {hint && <div className="text-[10px] text-muted-foreground mt-0.5">{hint}</div>}
    </Card>
  );
}

function relativeTime(iso) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const diff = Date.now() - t;
  if (diff < 0) return "just now";
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}
